import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReportType, ReportsPeriod } from '../../reports-flow.types';
import { Success, Summary } from './components';
import { getAccountsByEmail, sendBulkReport } from '../../../../../../../../../services/api';
import { useAppContext, useModalsContext } from '../../../../../../contexts';

import { CollectivePortfoliosProps } from './collective-portfolios.types';
import { ModalIds } from '../../../../../../contexts/modals/modals.types';
import { SuccessProps } from './components/success/success.types';
import { SummaryProps } from './components/summary/summary.types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const formatPeriodLabel = (startDate: Date, reportType: ReportType, t: (value: string) => string): string => {
  const dividedStartDate = dayjs(startDate).format('MMMM YYYY').split(' ');
  if (reportType === ReportType.monthly) {
    return `${t(dividedStartDate[0])} ${dividedStartDate[1]}`;
  } else {
    return `${dividedStartDate[1]} ${t('modals.reports.summary.year')}`;
  }
};

/**
 * useCollectivePortfoliosData hook
 * @description The hook which processes Reports CollectivePortfolios flow data
 *
 * @author Oleksii Medvediev
 * @category Hooks
 */
const useCollectivePortfoliosData = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'management' });
  const { user, dispatch: appDispatch } = useAppContext();
  const {
    data: { reportsCollectivePortfolios },
    dispatch,
  } = useModalsContext();

  const [minDate, setMinDate] = useState<string>();
  const [period, setPeriod] = useState<ReportsPeriod>();

  const flowData: CollectivePortfoliosProps | undefined = useMemo(
    () => reportsCollectivePortfolios,
    [reportsCollectivePortfolios],
  );

  const fetchAccountsByEmail = useCallback(async () => {
    if (flowData) {
      appDispatch({ type: 'TOGGLE_IS_LOADING' });

      try {
        const { data } = await getAccountsByEmail({
          emails: flowData.portfolios.map(({ clientEmailOwner }) => clientEmailOwner),
        });
        if (data) {
          const dates = [...data]
            .filter(({ portfolios }) =>
              flowData.portfolios.some(({ portfolioId }) => portfolios.some(({ id }) => id === portfolioId)),
            )
            .flatMap(({ dateAccountFunded, portfolios }) => [
              dateAccountFunded,
              ...portfolios.map(({ opened }) => opened),
            ]);
          setMinDate([...dates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]);
        }
      } catch (error) {
        toast.error('Failed to fetch accounts by email!');
        console.error(error);
      }

      appDispatch({ type: 'TOGGLE_IS_LOADING' });
    }
  }, [appDispatch, flowData]);

  useEffect(() => {
    if (!minDate) {
      fetchAccountsByEmail();
    }
  }, [fetchAccountsByEmail, minDate]);

  const handleCloseModal = useCallback(() => {
    dispatch({ type: 'HIDE_MODAL', payload: ModalIds.reportsCollectivePortfolios });
  }, [dispatch]);

  const setStage = useCallback(
    (stage: CollectivePortfoliosProps['currentStage']) =>
      flowData &&
      dispatch({
        type: 'UPDATE_MODAL_DATA',
        payload: {
          id: ModalIds.reportsCollectivePortfolios,
          data: {
            [ModalIds.reportsCollectivePortfolios]: { ...flowData, currentStage: stage },
          },
        },
      }),
    [dispatch, flowData],
  );

  const submitHandlers: Record<
    CollectivePortfoliosProps['currentStage'],
    SummaryProps['onSubmitStage'] | SuccessProps['onSubmitStage']
  > = useMemo(
    () => ({
      summary: async () => {
        if (user && flowData && period) {
          appDispatch({ type: 'TOGGLE_IS_LOADING' });
          try {
            const { message } = await sendBulkReport({
              clients: flowData.portfolios.map(
                ({ accountId, clientEmailOwner, clientNameOwner, portfolioId, portfolioEmail }) => ({
                  accountId,
                  email: portfolioEmail,
                  name: clientNameOwner,
                  portfolioId,
                }),
              ),
              start: dayjs(period.startDate).format('YYYY-MM-DD'),
              end: dayjs(period.endDate).format('YYYY-MM-DD'),
              reportType: ReportType.monthly,
            });
            if (message === 'ok' || message.includes('Duplicate entry')) {
              setStage('success');
            }
          } catch (error) {
            toast.error('Failed to process send bulk report request!');
            console.error(error);
          }
          appDispatch({ type: 'TOGGLE_IS_LOADING' });
        }
      },
      success: handleCloseModal,
    }),
    [appDispatch, flowData, handleCloseModal, period, setStage, user],
  );

  const stages: Record<CollectivePortfoliosProps['currentStage'], JSX.Element> | undefined = useMemo(
    () =>
      flowData && minDate
        ? {
            summary: (
              <Summary
                portfolios={flowData.portfolios}
                minDate={minDate}
                period={period}
                onClose={handleCloseModal}
                onSetPeriod={(value) => setPeriod(value)}
                onSubmitStage={submitHandlers.summary}
              />
            ),
            success: <Success portfolios={flowData.portfolios} t={t} onSubmitStage={submitHandlers.success} />,
          }
        : undefined,
    [flowData, handleCloseModal, minDate, period, submitHandlers.success, submitHandlers.summary, t],
  );

  return {
    stages,
    currentStage: useMemo(() => flowData?.currentStage, [flowData?.currentStage]),
  };
};

export { formatPeriodLabel, useCollectivePortfoliosData };
